import type { CSSProperties } from "react";
import type { TableColumn, TableVariant } from "@/types/table";
import { formatSmartValue } from "@/lib/tableFormatters";
import { getAriaSort, SortableColumnHeader } from "@/components/SortableColumnHeader";
import type { SortDir, SortState } from "@/lib/tableSort";

type UtilisationDataTableProps<T extends Record<string, unknown>> = {
  title: string;
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  stickyFirstColumn?: boolean;
  variant?: TableVariant;
  sort?: SortState;
  onSort?: (key: string, direction: SortDir | null) => void;
};

const matrixTextColumns = ["emp_name", "emp_category", "service_line", "legacy_office", "metric"];
const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

export function UtilisationDataTable<T extends Record<string, unknown>>({
  title,
  data,
  columns,
  loading = false,
  error = null,
  emptyMessage = "No records found",
  stickyFirstColumn = true,
  variant = "default",
  sort,
  onSort,
}: UtilisationDataTableProps<T>) {
  const rowCountLabel = loading ? "Loading" : `${data.length} rows`;
  const groupRow = buildHeaderGroupRow(columns);

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-label={title}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold text-card-foreground">{title}</h2>
        <span className="text-xs text-muted-foreground">{rowCountLabel}</span>
      </div>

      <div className="max-h-[340px] overflow-auto">
        <table className="w-max min-w-full table-auto border-separate border-spacing-0 text-xs">
          <colgroup>
            {columns.map((column, columnIndex) => (
              <col key={String(column.key)} style={getColumnStyle(column.key, columnIndex, columns.length, variant)} />
            ))}
          </colgroup>
          <thead>
            {groupRow && (
              <tr>
                {groupRow.map((cell, cellIndex) => {
                  if (cell.kind !== "leaf") {
                    return (
                      <th
                        key={`group-${cell.label}-${cellIndex}`}
                        colSpan={cell.span}
                        className="sticky top-0 z-20 h-9 whitespace-nowrap border-r border-white/10 bg-accent px-px py-2.5 text-center font-bold leading-tight text-accent-foreground"
                      >
                        {cell.label}
                      </th>
                    );
                  }
                  return (
                    <th
                      key={`group-leaf-${String(cell.column.key)}`}
                      rowSpan={2}
                      className={cx(
                        "sticky top-0 z-20 h-9 whitespace-nowrap border-r border-white/10 bg-accent px-px py-2.5 align-bottom font-bold leading-tight text-accent-foreground",
                        stickyFirstColumn && cellIndex === 0 && "left-0 z-30",
                        cellIndex === 0 && "pl-4",
                        getCellAlignClass(cellIndex),
                      )}
                      aria-sort={getAriaSort(cell.column.sortKey, sort)}
                    >
                      <SortableColumnHeader
                        label={cell.column.label}
                        sortKey={cell.column.sortKey}
                        sortType={cell.column.sortType}
                        sort={sort}
                        onSort={onSort}
                      />
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              {columns.map((column, columnIndex) => {
                // Ungrouped columns were already rendered above with rowSpan=2.
                if (groupRow && !column.group) return null;
                return (
                  <th
                    key={String(column.key)}
                    className={cx(
                      "sticky z-20 whitespace-nowrap border-r border-white/10 bg-accent px-px py-2.5 font-bold leading-tight text-accent-foreground",
                      groupRow ? "top-9" : "top-0",
                      stickyFirstColumn && columnIndex === 0 && !groupRow && "left-0 z-30",
                      columnIndex === 0 && "pl-4",
                      getCellAlignClass(columnIndex),
                    )}
                    aria-sort={getAriaSort(column.sortKey, sort)}
                  >
                    <SortableColumnHeader
                      label={column.label}
                      sortKey={column.sortKey}
                      sortType={column.sortType}
                      sort={sort}
                      onSort={onSort}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? <TableMessage columns={columns.length} message="Loading data..." /> : null}
            {!loading && error ? <TableMessage columns={columns.length} message={error} tone="error" /> : null}
            {!loading && !error && data.length === 0 ? (
              <TableMessage columns={columns.length} message={emptyMessage} />
            ) : null}
            {!loading && !error
              ? data.map((row, rowIndex) => (
                  <tr key={getRowKey(row, rowIndex)} className="even:bg-muted/35 hover:bg-secondary/50">
                    {columns.map((column, columnIndex) => {
                      const value = row[String(column.key)];
                      const isSticky = stickyFirstColumn && columnIndex === 0;
                      return (
                        <td
                          key={String(column.key)}
                          className={cx(
                            "border-b px-px py-2.5 text-foreground",
                            variant === "matrix" && "border-r tabular-nums",
                            isTextColumn(column.key, columnIndex)
                              ? "whitespace-normal break-words leading-snug"
                              : "whitespace-nowrap",
                            isSticky && "sticky left-0 z-10 bg-card font-medium shadow-[1px_0_0_hsl(var(--border))]",
                            isSticky && rowIndex % 2 === 1 && "bg-muted",
                            columnIndex === 0 && "pl-4",
                            getCellAlignClass(columnIndex),
                            column.className,
                          )}
                        >
                          {formatCell(value, row, column)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TableMessage({ columns, message, tone }: { columns: number; message: string; tone?: "error" }) {
  return (
    <tr>
      <td
        className={`h-44 bg-card text-center ${tone === "error" ? "text-destructive" : "text-muted-foreground"}`}
        colSpan={Math.max(columns, 1)}
      >
        {message}
      </td>
    </tr>
  );
}

function formatCell<T extends Record<string, unknown>>(value: unknown, row: T, column: TableColumn<T>) {
  const rendered = column.format ? column.format(value, row) : formatSmartValue(value);
  return rendered ?? "-";
}

function getRowKey(row: Record<string, unknown>, rowIndex: number): string {
  const stable = row.id ?? row._id ?? row.metric ?? row.emp_name ?? row.monthLabel ?? row.month_label;
  return stable ? `${String(stable)}-${rowIndex}` : String(rowIndex);
}

function getCellAlignClass(columnIndex: number): string {
  return columnIndex === 0 ? "text-left" : "text-center";
}

type HeaderGroupCell<T extends Record<string, unknown>> =
  | { kind: "leaf"; column: TableColumn<T> }
  | { kind: "span"; label: string; span: number };

function buildHeaderGroupRow<T extends Record<string, unknown>>(
  columns: TableColumn<T>[],
): HeaderGroupCell<T>[] | null {
  if (!columns.some((column) => column.group)) return null;

  const cells: HeaderGroupCell<T>[] = [];
  let i = 0;
  while (i < columns.length) {
    const column = columns[i];
    if (!column.group) {
      cells.push({ kind: "leaf", column });
      i += 1;
      continue;
    }
    let span = 1;
    while (i + span < columns.length && columns[i + span].group === column.group) {
      span += 1;
    }
    cells.push({ kind: "span", label: column.group, span });
    i += span;
  }
  return cells;
}

function isTextColumn(key: TableColumn<Record<string, unknown>>["key"], columnIndex: number): boolean {
  const normalized = String(key).toLowerCase();
  return columnIndex === 0 || matrixTextColumns.includes(normalized);
}

function getColumnStyle(
  key: TableColumn<Record<string, unknown>>["key"],
  columnIndex: number,
  columnCount: number,
  variant: TableVariant,
): CSSProperties {
  if (variant !== "matrix") return {};

  const normalized = String(key).toLowerCase();
   if (normalized === "metric") { return { width: "max-content" };}
  if (normalized === "emp_name") return { width: "1%", minWidth: "11rem", maxWidth: "15rem" };
  if (["emp_category", "service_line", "legacy_office"].includes(normalized)) {
    return { width: "1%", minWidth: "8.75rem", maxWidth: "12.75rem" };
  }
  if (columnCount <= 8) return { width: "1%", minWidth: "6.25rem" };
  if (columnIndex === 1) return { width: "1%", minWidth: "6.25rem" };
  return { width: "1%", minWidth: "5.5rem" };
}
